/**
 * FIELD-LIVE-SENSOR-METRICS-0001 — shared live-metric interpreter.
 *
 * Canonical measurements (catalog) are primary. Legacy scalar aliases are a
 * compatibility fallback ONLY. Nothing here fabricates a value, infers an
 * engineering conversion (except the already-proven kPa→bar), or turns a raw
 * ADC/pulse/relay reading into a farmer-facing engineering metric.
 *
 * Pure helpers only — no React, no network, no side effects.
 */

import type { CanonicalMeasurement, IoTNode, NodeLatest } from './iotApi';

export type Measures = NodeLatest['measures'];

/** Stable semantic metric keys shared by Devices, Dashboard and Digital Twin. */
export type MetricKey =
  | 'flow'
  | 'pressure'
  | 'soilMoisture'
  | 'soilEc'
  | 'soilTemperature'
  | 'airTemperature'
  | 'airHumidity'
  | 'battery';

export interface LiveMetric {
  key: MetricKey;
  /** Finite numeric value in the canonical display unit (bar / L/min / % / °C / V / µS/cm). */
  value: number;
}

export type StateKind = 'valve' | 'pump' | 'motor';

export interface NodeState {
  kind: StateKind;
  value: number; // 0 or 1
}

export interface ValveChannelState {
  channel: string;
  open: boolean;
}

/**
 * One AGLW4R01 relay contact.
 *
 * `on === null` means the channel was NOT reported in this frame — it is
 * UNKNOWN, never OFF. Collapsing an absent channel to OFF would claim the
 * contact is open when the device simply did not tell us.
 */
export interface RelayChannelState {
  /** Manufacturer downlink channel label: K1 | K2 | K3 | K4. */
  channel: RelayChannelId;
  on: boolean | null;
}

export type RelayChannelId = 'K1' | 'K2' | 'K3' | 'K4';

/** The four operational relay channels, in manufacturer order. */
export const RELAY_CHANNEL_IDS: readonly RelayChannelId[] = ['K1', 'K2', 'K3', 'K4'];

/**
 * Channel label → decoded field name, mirroring the backend's authoritative
 * `CHANNEL_TO_RELAY_FIELD` (backend/agrisafe/decoders/relay_4ch.py). Kept
 * identical on purpose: a divergence here would attribute a pump's state to
 * the wrong physical contact.
 */
const RELAY_CHANNEL_FIELD: Record<RelayChannelId, string> = {
  K1: 'relay_1_state',
  K2: 'relay_2_state',
  K3: 'relay_3_state',
  K4: 'relay_4_state',
};

/** A real zero is a valid measurement — only non-finite values are rejected. */
export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const canonicalList = (measures: Measures | undefined): CanonicalMeasurement[] =>
  (measures && Array.isArray(measures.canonical_measurements)
    ? measures.canonical_measurements
    : []);

/**
 * Exact measurement_id + unit_id match over canonical measurements, optionally
 * narrowed to one channel. Returns the first finite numeric value, or undefined.
 */
export function canonicalNumber(
  measures: Measures | undefined,
  measurementId: string,
  unitId: string,
  channelId?: string,
): number | undefined {
  for (const cm of canonicalList(measures)) {
    if (cm.measurement_id !== measurementId) continue;
    if (cm.unit_id !== unitId) continue;
    if (channelId !== undefined && cm.channel_id !== channelId) continue;
    if (isFiniteNumber(cm.value)) return cm.value;
  }
  return undefined;
}

/** First finite numeric value among legacy scalar aliases, or undefined. */
export function legacyNumber(
  measures: Measures | undefined,
  aliases: readonly string[],
): number | undefined {
  if (!measures) return undefined;
  const record = measures as unknown as Record<string, unknown>;
  for (const alias of aliases) {
    const value = record[alias];
    if (isFiniteNumber(value)) return value;
  }
  return undefined;
}

/** Explicit semantic state (0/1) among legacy aliases, or undefined. */
export function explicitState(
  measures: Measures | undefined,
  aliases: readonly string[],
): number | undefined {
  const value = legacyNumber(measures, aliases);
  return value === 0 || value === 1 ? value : undefined;
}

/** The only engineering conversion this layer permits: kilopascal → bar. */
export const pressureKpaToBar = (kpa: number): number => kpa / 100;

// ---------------------------------------------------------------------------
// Individual metric getters — canonical first, legacy compatibility fallback.
// ---------------------------------------------------------------------------

/** Calibrated flow in L/min. Never derived from flow_pulse_frequency. */
export function getFlow(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'flow_rate', 'liter_per_minute')
    ?? legacyNumber(measures, ['flow_velocity_lpm', 'flow_l_min', 'flow_lpm'])
  );
}

/** Pressure in bar — canonical kPa/100, else pressure_bar. Never from sensor_voltage. */
export function getPressure(measures: Measures | undefined): number | undefined {
  const kpa = canonicalNumber(measures, 'pressure', 'kilopascal');
  if (kpa !== undefined) return pressureKpaToBar(kpa);
  return legacyNumber(measures, ['pressure_bar']);
}

/** Soil moisture %. Never derived from soil_moisture_raw ADC. */
export function getSoilMoisture(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'soil_moisture', 'percent')
    ?? legacyNumber(measures, ['soil_moisture_pct', 'soil_moisture', 'moisture_pct'])
  );
}

/** Soil electrical conductivity µS/cm. */
export function getSoilEc(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'soil_electrical_conductivity', 'microsiemens_per_centimeter')
    ?? legacyNumber(measures, ['soil_ec_us_cm'])
  );
}

/** Soil temperature °C. */
export function getSoilTemperature(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'soil_temperature', 'degree_celsius')
    ?? legacyNumber(measures, ['soil_temperature_c'])
  );
}

/** Air temperature °C. */
export function getAirTemperature(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'air_temperature', 'degree_celsius')
    ?? legacyNumber(measures, ['air_temperature_c'])
  );
}

/** Air relative humidity %. */
export function getAirHumidity(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'air_relative_humidity', 'percent')
    ?? legacyNumber(measures, ['air_humidity_pct', 'air_humidity_percent'])
  );
}

/** Battery voltage V. */
export function getBattery(measures: Measures | undefined): number | undefined {
  return (
    canonicalNumber(measures, 'battery_voltage', 'volt')
    ?? legacyNumber(measures, ['battery_v'])
  );
}

/** Pump/motor state — explicit pump_state / motor_state ONLY, never relay_state. */
export function getPumpState(measures: Measures | undefined): number | undefined {
  return explicitState(measures, ['pump_state', 'motor_state']);
}

// ---------------------------------------------------------------------------
// AGLW4R01 relay contacts (FIELD-HW-RELAY-0001).
//
// STRICT and UNKNOWN-preserving: only an exact binary 0/1 is a state. A
// missing, null, non-finite, out-of-range or non-numeric field yields `null`
// (UNKNOWN) and NEVER OFF. The backend codec already fails closed on a byte
// outside {0,1}; this mirrors that discipline on the read side.
//
// A relay contact proves the CONTACT, not the motor shaft (section 8). No
// helper here is allowed to return a pump/motor/irrigation state.
// ---------------------------------------------------------------------------

/** True when the model physically has relay contacts. */
export const isRelayCapable = (model: string | null | undefined): boolean =>
  model === 'relay_4ch';

/** Exact binary 0/1 → boolean; anything else → null (UNKNOWN, never OFF). */
const binaryContact = (value: unknown): boolean | null => {
  if (value === 0) return false;
  if (value === 1) return true;
  return null;
};

/**
 * Relay contacts read from a telemetry `measures` object (history frames).
 * Always returns all four channels so the caller renders a stable K1..K4 row;
 * unreported channels are `on: null` (UNKNOWN).
 */
export function getRelayChannels(measures: Measures | undefined): RelayChannelState[] {
  const record = (measures ?? undefined) as unknown as Record<string, unknown> | undefined;
  return RELAY_CHANNEL_IDS.map((channel) => ({
    channel,
    on: record ? binaryContact(record[RELAY_CHANNEL_FIELD[channel]]) : null,
  }));
}

/**
 * Relay contacts for a LIVE node.
 *
 * Precedence mirrors the other node-aware helpers: `latest.measures` first,
 * then the node ROOT (where the ingestion pipeline writes live relay state for
 * this legacy-runtime model). A root field never overrides an available
 * measures value, and a channel absent from BOTH stays UNKNOWN.
 *
 * Returns `undefined` for a non-relay model rather than four UNKNOWN channels,
 * so callers cannot render a relay row for a device that has no contacts.
 */
export function getNodeRelayChannels(node: IoTNode): RelayChannelState[] | undefined {
  if (!isRelayCapable(node.model)) return undefined;

  const fromMeasures = getRelayChannels(node.latest?.measures);
  const root = node as unknown as Record<string, unknown>;

  return fromMeasures.map((state) => {
    if (state.on !== null) return state;
    return {
      channel: state.channel,
      on: binaryContact(root[RELAY_CHANNEL_FIELD[state.channel]]),
    };
  });
}

/**
 * The state of ONE commissioned relay channel, or `null` when unknown.
 *
 * `channel` is the canonical `relay_channel` from the backend pump binding. A
 * null/blank/unrecognised channel yields `null`: we never fall back to K1, and
 * an uncommissioned pump therefore stays UNKNOWN instead of borrowing another
 * contact's state.
 */
export function getRelayChannelState(
  node: IoTNode | undefined,
  channel: string | null | undefined,
): boolean | null {
  if (!node) return null;
  if (typeof channel !== 'string') return null;
  const normalized = channel.trim().toUpperCase();
  if (!RELAY_CHANNEL_IDS.includes(normalized as RelayChannelId)) return null;
  const channels = getNodeRelayChannels(node);
  if (!channels) return null;
  return channels.find((state) => state.channel === normalized)?.on ?? null;
}

/**
 * Per-channel canonical valve states (e.g. asvwf01 valve_1..valve_4).
 *
 * STRICT: a canonical entry only counts as a valve state when it has
 * measurement_id === 'valve_state', unit_id === 'unitless', a binary 0/1
 * value, and an operational ASVWF01 channel_id. Anything else (wrong unit,
 * -1/2/0.5/NaN/"1", missing/empty channel, or a channel outside valve_1..valve_4
 * such as valve_0 / valve_5 / valve_10 / "system") is ignored — never inferred
 * as open or closed.
 */
export function getValveChannels(measures: Measures | undefined): ValveChannelState[] {
  const channels: ValveChannelState[] = [];
  for (const cm of canonicalList(measures)) {
    if (cm.measurement_id !== 'valve_state') continue;
    if (cm.unit_id !== 'unitless') continue;
    if (cm.value !== 0 && cm.value !== 1) continue;
    const channel = cm.channel_id;
    if (typeof channel !== 'string' || channel.trim().length === 0) continue;
    // STRICT ASVWF01 boundary: only the four operational valve channels count.
    if (valveChannelOrdinal(channel) === undefined) continue;
    channels.push({ channel, open: cm.value === 1 });
  }
  return channels;
}

/** 1-based ordinal from an operational ASVWF01 valve channel id
 *  (valve_1 → 1 … valve_4 → 4); anything else (valve_0, valve_5, valve_10,
 *  "system", empty, unknown) is undefined. */
export function valveChannelOrdinal(channel: string): number | undefined {
  const match = /^valve_(\d+)$/.exec(channel);
  if (!match) return undefined;
  const ordinal = Number(match[1]);
  return Number.isFinite(ordinal) && ordinal >= 1 && ordinal <= 4 ? ordinal : undefined;
}

/**
 * Aggregated valve state (0/1): any valid canonical channel open → 1, all
 * closed → 0. Falls back to the legacy valve_state scalar. Absent → undefined.
 */
export function getValveState(measures: Measures | undefined): number | undefined {
  const channels = getValveChannels(measures);
  if (channels.length > 0) {
    return channels.some((channel) => channel.open) ? 1 : 0;
  }
  return explicitState(measures, ['valve_state']);
}

// ---------------------------------------------------------------------------
// Node-aware helpers — canonical/latest FIRST, then valve_water_flow root
// fields (legacy live state stored on the node root when latest is null).
// A root field never overrides an available canonical/latest value.
// ---------------------------------------------------------------------------

/** Flow in L/min — canonical/latest first, then valve_water_flow root. */
export function getNodeFlow(node: IoTNode): number | undefined {
  const latest = getFlow(node.latest?.measures);
  if (latest !== undefined) return latest;
  if (node.model === 'valve_water_flow' && isFiniteNumber(node.flow_velocity_lpm)) {
    return node.flow_velocity_lpm;
  }
  return undefined;
}

/** Battery voltage V — canonical/latest first, then valve_water_flow root. */
export function getNodeBattery(node: IoTNode): number | undefined {
  const latest = getBattery(node.latest?.measures);
  if (latest !== undefined) return latest;
  if (node.model === 'valve_water_flow' && isFiniteNumber(node.battery_v)) {
    return node.battery_v;
  }
  return undefined;
}

/** Valve state — canonical/latest first, then exact valve_water_flow root 0/1. */
export function getNodeValveState(node: IoTNode): number | undefined {
  const latest = getValveState(node.latest?.measures);
  if (latest !== undefined) return latest;
  if (node.model === 'valve_water_flow') {
    const root = node.valve_state;
    if (root === 0 || root === 1) return root;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Model-aware aggregation.
// ---------------------------------------------------------------------------

const VALVE_CAPABLE_MODELS = new Set(['asvwf01', 'valve_water_flow']);

export const isValveCapable = (model: string | null | undefined): boolean =>
  typeof model === 'string' && VALVE_CAPABLE_MODELS.has(model);

/**
 * The list of meaningful live metrics for a node's model. Missing metrics are
 * omitted; a node with no supported metrics returns an empty list (callers
 * render "—"). Future/inactive models return nothing — no fabricated values.
 */
export function getNodeMetrics(node: IoTNode): LiveMetric[] {
  const measures = node.latest?.measures;
  const metrics: LiveMetric[] = [];
  const push = (key: MetricKey, value: number | undefined): void => {
    if (value !== undefined) metrics.push({ key, value });
  };

  switch (node.model) {
    case 'soil_air_ecth':
      push('soilMoisture', getSoilMoisture(measures));
      push('soilEc', getSoilEc(measures));
      push('soilTemperature', getSoilTemperature(measures));
      push('airTemperature', getAirTemperature(measures));
      push('airHumidity', getAirHumidity(measures));
      push('battery', getBattery(measures));
      break;
    case 'pipe_pressure':
      push('pressure', getPressure(measures));
      push('battery', getBattery(measures));
      break;
    case 'asvwf01':
      push('flow', getFlow(measures));
      push('battery', getBattery(measures));
      break;
    case 'valve_water_flow':
      push('flow', getNodeFlow(node));
      push('battery', getNodeBattery(node));
      break;
    case 'relay_4ch':
      // Relay state is never presented as a farmer metric.
      break;
    default:
      // Future/inactive models (ultrasonic, leaf_moisture, adc_4ch, ct_monitor)
      // and anything unknown: render nothing rather than fabricate a value.
      break;
  }

  return metrics;
}

/**
 * Model-aware equipment state. Valve state only for valve-capable models;
 * pump/motor only from explicit semantic state. relay_state never implies pump,
 * and passive sensors never show Open/Closed.
 */
export function getNodeState(node: IoTNode): NodeState | undefined {
  const measures = node.latest?.measures;

  if (isValveCapable(node.model)) {
    const valve = getNodeValveState(node);
    if (valve !== undefined) return { kind: 'valve', value: valve };
  }

  const pump = explicitState(measures, ['pump_state']);
  if (pump !== undefined) return { kind: 'pump', value: pump };

  const motor = explicitState(measures, ['motor_state']);
  if (motor !== undefined) return { kind: 'motor', value: motor };

  return undefined;
}
